// PreviewInteriorQuatation.js - full-screen, print/PDF-style preview of the quotation being
// built in CreateInteriorQuatation.js. Renders the live (possibly unsaved/incomplete) form
// state as a single bordered document - Date/Client/Project header, one bordered table per
// section (Room | Unit Name | Width | Height | Depth | SFT | Rate | Total | Details for priced sections,
// Room/Category | Unit Name | Qty | Details for informational ones), the totals block,
// payment schedule, and the four template text blocks as numbered rows - closely mirroring
// the reference Tesor Designs quotation spreadsheet. "Download PDF" rasterizes this document
// with html2canvas and slices it into A4 pages via jsPDF; no data is saved by opening this.
//
// The actual document markup lives in the exported InteriorQuotationDocument (a forwardRef,
// so its rendered DOM node can be captured by html2canvas) so interiorQuotationPdf.js can
// reuse the exact same layout to export a *saved* record from the quotations list without
// opening this modal - see downloadInteriorQuotationPdf.
import DOMPurify from 'dompurify';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Download, Eye, Loader2, X } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import {
  applyTenantLogoDataUrlToNode,
  getTenantLogoDataUrl,
  getTenantLogoUrl,
  isTenantLogoDataUrl,
  stampTenantLogoOnPdf,
} from '../../services/tenantLogoApi';
import { resolveQuotationFilePreviewUrl, fetchImageAsDataUrl } from './interiorQuotationApi';
import { computeClusterSizes, computeClusterStarts, toNumber } from './interiorQuotationModel';

const formatCurrency = (amount = 0) =>
  Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(Number.isFinite(amount) ? amount : 0);

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const sectionLetter = (index) => String.fromCharCode(65 + index);

const hasEnteredDimension = (value) => {
  if (value == null) return false;
  const text = String(value).trim();
  return text !== '';
};

/** Per priced section: only show dim columns that have at least one entered value. */
const getSectionDimensionVisibility = (section) => {
  const rooms = section?.rooms || [];
  let showWidth = false;
  let showHeight = false;
  let showDepth = false;

  for (const room of rooms) {
    for (const item of room.items || []) {
      if (!showWidth && hasEnteredDimension(item.widthFt)) showWidth = true;
      if (!showHeight && hasEnteredDimension(item.heightFt)) showHeight = true;
      if (!showDepth && hasEnteredDimension(item.depthFt)) showDepth = true;
      if (showWidth && showHeight && showDepth) {
        return { showWidth, showHeight, showDepth };
      }
    }
  }

  return { showWidth, showHeight, showDepth };
};

// Splits a RichTextEditor HTML value (one <p>/<li>/... per line) into an ordered array of
// sanitized inner-HTML strings, one per top-level block - so template text renders as
// numbered rows (like the source spreadsheet) instead of one opaque HTML blob.
const htmlToNumberedRows = (html) => {
  if (!html) return [];
  const container = document.createElement('div');
  container.innerHTML = DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
  const blocks = Array.from(container.children).filter((el) => (el.textContent || '').trim());
  if (!blocks.length) {
    const text = (container.textContent || '').trim();
    return text ? [text] : [];
  }
  return blocks.map((el) => el.innerHTML);
};

const thStyle = 'border border-gray-800 bg-sky-100 px-2 py-1.5 text-left text-[11px] font-bold text-gray-900';
const tdStyle = 'border border-gray-800 px-2 py-1.5 align-top text-[11px] text-gray-900';
const sectionTitleStyle =
  'border border-gray-800 bg-blue-100 px-2 py-1.5 text-left text-[12px] font-bold uppercase text-gray-900';

const openImageInNewWindow = (url, title = 'Image') => {
  if (!url) return false;

  // Do not pass noopener in windowFeatures — that makes window.open return null,
  // which blocks writing an <img> into the tab (needed for data: URLs).
  const win = window.open('about:blank', '_blank');
  if (!win) return false;

  try {
    win.opener = null;
    const doc = win.document;
    doc.open();
    doc.write(
      '<!DOCTYPE html><html><head><meta charset="utf-8"><title></title>' +
        '<style>html,body{margin:0;height:100%;background:#111}' +
        'body{display:flex;align-items:center;justify-content:center}' +
        'img{max-width:100%;max-height:100vh;object-fit:contain}</style></head>' +
        '<body></body></html>'
    );
    doc.close();
    doc.title = title;
    const img = doc.createElement('img');
    img.src = url;
    img.alt = title;
    doc.body.appendChild(img);
    return true;
  } catch {
    win.close();
    // Fallback for http(s) URLs if document write fails
    if (/^https?:\/\//i.test(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return true;
    }
    return false;
  }
};

// Canvas-drawn eye (PNG data URL) — Lucide SVGs do not survive html2canvas in the PDF.
const getEyeIconDataUrl = () => {
  if (typeof document === 'undefined') return '';
  if (getEyeIconDataUrl._cache) return getEyeIconDataUrl._cache;
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d');
  ctx.strokeStyle = '#0d9488';
  ctx.fillStyle = '#0d9488';
  ctx.lineWidth = 5;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(6, 32);
  ctx.bezierCurveTo(20, 10, 44, 10, 58, 32);
  ctx.bezierCurveTo(44, 54, 20, 54, 6, 32);
  ctx.closePath();
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(32, 32, 11, 0, Math.PI * 2);
  ctx.fill();
  getEyeIconDataUrl._cache = canvas.toDataURL('image/png');
  return getEyeIconDataUrl._cache;
};

// Registry of full-size images for PDF export (key → { dataUrl, title }).
// Avoid stuffing huge data URLs into DOM attributes.
let pdfExportImages = new Map();

export const resetPdfExportImages = () => {
  pdfExportImages = new Map();
};

export const waitForPdfImageCells = async (node, timeoutMs = 30000) => {
  if (!node) return;
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    const logoPending = node.querySelector('[data-tenant-logo-ready="0"]');
    const cells = node.querySelectorAll('[data-pdf-image-cell="1"]');
    const imagePending =
      cells.length > 0 &&
      Array.from(cells).some((el) => el.getAttribute('data-ready') !== '1');

    if (!logoPending && !imagePending) {
      const imgs = Array.from(
        node.querySelectorAll(
          '[data-pdf-image-cell="1"] img, [data-tenant-logo="1"] img'
        )
      );
      if (!imgs.some((img) => img.src && !img.complete)) return;
    }
    await new Promise((r) => setTimeout(r, 80));
  }
};

const gatherPdfImageHotspots = (node) => {
  if (!node) return [];
  const nodeRect = node.getBoundingClientRect();
  return Array.from(node.querySelectorAll('[data-pdf-image-key]'))
    .map((el) => {
      const key = el.getAttribute('data-pdf-image-key');
      if (!key || !pdfExportImages.has(key)) return null;
      const r = el.getBoundingClientRect();
      return {
        key,
        x: r.left - nodeRect.left + node.scrollLeft,
        y: r.top - nodeRect.top + node.scrollTop,
        w: Math.max(r.width, 18),
        h: Math.max(r.height, 18),
      };
    })
    .filter(Boolean);
};

/** Fit a data-URL image onto an A4 page (mm), centered. */
const addFullImagePage = (pdf, dataUrl, title = '') => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 12;
  const maxW = pageWidth - margin * 2;
  const maxH = pageHeight - margin * 2 - (title ? 10 : 0);

  pdf.addPage();
  if (title) {
    pdf.setFontSize(11);
    pdf.setTextColor(40);
    pdf.text(String(title).slice(0, 80), margin, margin);
  }

  const format = String(dataUrl).startsWith('data:image/jpeg') ? 'JPEG' : 'PNG';
  // Probe natural size via Image
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const iw = img.naturalWidth || 1;
      const ih = img.naturalHeight || 1;
      const scale = Math.min(maxW / iw, maxH / ih, 1);
      const w = iw * scale;
      const h = ih * scale;
      const x = (pageWidth - w) / 2;
      const y = margin + (title ? 8 : 0) + (maxH - h) / 2;
      try {
        pdf.addImage(dataUrl, format, x, y, w, h);
      } catch {
        try {
          pdf.addImage(dataUrl, 'PNG', x, y, w, h);
        } catch {
          // skip broken image
        }
      }
      resolve();
    };
    img.onerror = () => resolve();
    img.src = dataUrl;
  });
};

// Per-item reference image.
// Preview: lucide eye → full image in a separate browser window.
// PDF: eye icon in the table; click jumps to a full-image page (works on any system offline).
const ImageCell = ({ image, siteId, forExport = false, imageKey = '' }) => {
  const [isOpening, setIsOpening] = useState(false);
  const [exportSrc, setExportSrc] = useState(image?.dataUrl || null);
  const [exportReady, setExportReady] = useState(() => !forExport);
  const hasImageRef = Boolean(image?.dataUrl || image?.s3_key || image?.file_id || image?.name);
  const canOpen = Boolean(image?.dataUrl || (image?.file_id && siteId));
  const registryKey =
    imageKey || image?.file_id || image?.name || `img-${Math.random().toString(36).slice(2)}`;

  useEffect(() => {
    if (!forExport) {
      setExportReady(true);
      return undefined;
    }

    let cancelled = false;

    const resolve = async () => {
      try {
        let dataUrl = null;
        if (image?.dataUrl) {
          dataUrl = image.dataUrl;
        } else if (image?.file_id && siteId) {
          const url = await resolveQuotationFilePreviewUrl(siteId, image.file_id, {
            forceRefresh: true,
          });
          if (url) {
            try {
              dataUrl = await fetchImageAsDataUrl(url);
            } catch {
              dataUrl = null;
            }
          }
        }
        if (!cancelled) {
          setExportSrc(dataUrl);
          if (dataUrl) {
            pdfExportImages.set(registryKey, {
              dataUrl,
              title: image?.name || 'Line item image',
            });
          }
          setExportReady(true);
        }
      } catch {
        if (!cancelled) setExportReady(true);
      }
    };

    setExportReady(false);
    resolve();
    return () => {
      cancelled = true;
    };
  }, [forExport, image?.dataUrl, image?.file_id, image?.name, siteId, registryKey]);

  const handleOpenImage = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isOpening || !canOpen) return;

    setIsOpening(true);
    try {
      let url = image?.dataUrl || null;

      if (!url && image?.file_id && siteId) {
        url = await resolveQuotationFilePreviewUrl(siteId, image.file_id, {
          forceRefresh: true,
        });
      }

      if (!url) {
        alert('Unable to open this image. Try again in a moment.');
        return;
      }
      openImageInNewWindow(url, image?.name || 'Image Preview');
    } catch {
      alert('Unable to open this image. Try again in a moment.');
    } finally {
      setIsOpening(false);
    }
  };

  const linkClass = `inline-flex h-6 w-6 items-center justify-center rounded border bg-white transition-colors ${
    canOpen
      ? 'border-teal-300 text-teal-600 hover:border-teal-500 hover:text-teal-800'
      : 'border-gray-300 text-gray-400'
  }`;

  if (forExport) {
    const hasFull = Boolean(exportSrc);
    return (
      <td
        className={`${tdStyle} text-center`}
        data-pdf-image-cell="1"
        data-ready={exportReady ? '1' : '0'}
        {...(hasFull ? { 'data-pdf-image-key': registryKey } : {})}
      >
        {hasImageRef || canOpen || hasFull ? (
          <img
            src={getEyeIconDataUrl()}
            alt="View full image"
            width={20}
            height={20}
            style={{ display: 'inline-block', verticalAlign: 'middle' }}
          />
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
    );
  }

  return (
    <td className={`${tdStyle} text-center`}>
      {canOpen ? (
        <button
          type="button"
          onClick={handleOpenImage}
          disabled={isOpening}
          title={image?.name || 'Open full image in new window'}
          className={`${linkClass} disabled:cursor-wait disabled:opacity-60`}
        >
          {isOpening ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Eye className="h-3.5 w-3.5" />
          )}
        </button>
      ) : (
        <span
          title={hasImageRef ? 'Image link unavailable' : 'No image'}
          className={`${linkClass} cursor-default`}
        >
          <Eye className="h-3.5 w-3.5" />
        </span>
      )}
    </td>
  );
};

const renderRoomRows = (
  room,
  isPriced,
  siteId,
  forExport = false,
  dimVisibility = { showWidth: true, showHeight: true, showDepth: true }
) => {
  const items = room.items;
  const { showWidth, showHeight, showDepth } = dimVisibility;

  if (!isPriced) {
    return items.map((item, idx) => (
      <tr key={item.id}>
        {idx === 0 && (
          <td className={`${tdStyle} bg-gray-50 font-bold uppercase`} rowSpan={items.length}>
            {room.name || '—'}
          </td>
        )}
        <td className={tdStyle}>{item.unitName || '—'}</td>
        <td className={`${tdStyle} text-right`}>{item.qty || ''}</td>
        <td className={tdStyle}>{item.details || ''}</td>
        <ImageCell
          image={item.image}
          siteId={siteId}
          forExport={forExport}
          imageKey={item.id || item.image?.file_id || `row-${idx}`}
        />
      </tr>
    ));
  }

  const detStarts = computeClusterStarts(items, 'mergeDetailsUp');
  const detSizes = computeClusterSizes(detStarts);
  const totStarts = computeClusterStarts(items, 'mergeTotalUp');
  const totSizes = computeClusterSizes(totStarts);

  return items.map((item, idx) => {
    const isDetOwner = detStarts[idx] === idx;
    const isTotOwner = totStarts[idx] === idx;
    return (
      <tr key={item.id}>
        {idx === 0 && (
          <td className={`${tdStyle} bg-gray-50 font-bold uppercase`} rowSpan={items.length}>
            {room.name || '—'}
          </td>
        )}
        <td className={tdStyle}>{item.unitName || '—'}</td>
        {showWidth && <td className={`${tdStyle} text-right`}>{item.widthFt || ''}</td>}
        {showHeight && <td className={`${tdStyle} text-right`}>{item.heightFt || ''}</td>}
        {showDepth && <td className={`${tdStyle} text-right`}>{item.depthFt || ''}</td>}
        <td className={`${tdStyle} text-right`}>{item.qty || ''}</td>
        <td className={`${tdStyle} text-right`}>
          {item.ratePerUnit !== '' && item.ratePerUnit != null
            ? formatCurrency(toNumber(item.ratePerUnit))
            : ''}
        </td>
        {isTotOwner && (
          <td className={`${tdStyle} text-right font-semibold`} rowSpan={totSizes[idx]}>
            {formatCurrency(toNumber(item.unitTotal))}
          </td>
        )}
        {isDetOwner && (
          <td className={tdStyle} rowSpan={detSizes[idx]}>
            {item.details || ''}
          </td>
        )}
        <ImageCell
          image={item.image}
          siteId={siteId}
          forExport={forExport}
          imageKey={item.id || item.image?.file_id || `row-${idx}`}
        />
      </tr>
    );
  });
};

// The printable document itself - a forwardRef so callers (the modal below, or the headless
// PDF export in interiorQuotationPdf.js) can grab the rendered DOM node for html2canvas.
export const InteriorQuotationDocument = React.forwardRef(
  (
    {
      header,
      sections,
      pricedSectionTotals,
      totalPriced,
      totalAfterDiscount,
      designFeePercent,
      designFeeAmount,
      amountWithDesignFee,
      gstPercent,
      gstAmount,
      finalAmount,
      paymentRows,
      templates,
      invoiceLabel,
      documentVersion,
      siteId,
      forExport = false,
      /** Optional preloaded org logo (data URL preferred for PDF). */
      logoUrl: logoUrlProp = undefined,
    },
    ref
  ) => {
    const [tenantLogoUrl, setTenantLogoUrl] = useState(
      logoUrlProp === undefined ? null : logoUrlProp
    );
    const [logoReady, setLogoReady] = useState(logoUrlProp !== undefined);

    useEffect(() => {
      if (logoUrlProp !== undefined) {
        setTenantLogoUrl(logoUrlProp);
        setLogoReady(true);
        return undefined;
      }

      let cancelled = false;
      setLogoReady(false);

      (async () => {
        try {
          const url = await getTenantLogoUrl();
          if (!cancelled) {
            setTenantLogoUrl(url);
          }
        } catch (error) {
          console.warn('[InteriorQuotation] Failed to load tenant logo:', error);
          if (!cancelled) {
            setTenantLogoUrl(null);
          }
        } finally {
          if (!cancelled) {
            setLogoReady(true);
          }
        }
      })();

      return () => {
        cancelled = true;
      };
    }, [logoUrlProp]);

    const termsBlocks = useMemo(
      () => ({
        generalTerms: htmlToNumberedRows(templates?.generalTerms),
        materialSpec: htmlToNumberedRows(templates?.materialSpec),
        designTerms: htmlToNumberedRows(templates?.designTerms),
        importantNotes: htmlToNumberedRows(templates?.importantNotes),
      }),
      [templates]
    );

    const templateSections = [
      ['General Terms and Condition', termsBlocks.generalTerms],
      ['Material Specification - Premium Segment', termsBlocks.materialSpec],
      ['Design Terms and Condition', termsBlocks.designTerms],
      ['Important Notes', termsBlocks.importantNotes],
    ];

    const versionLabel = (() => {
      if (!documentVersion) return '';
      const v = String(documentVersion).trim();
      if (!v) return '';
      return v.toLowerCase().startsWith('v') ? v : `v${v}`;
    })();
    const companyRowSpan = versionLabel ? 5 : 4;
    const companyLabel = (header?.companyName || 'Company').toUpperCase();

    return (
      <div
        ref={ref}
        className="mx-auto bg-white text-gray-900"
        style={{ width: '210mm', minHeight: '297mm', padding: '10mm', boxSizing: 'border-box' }}
        data-tenant-logo-ready={logoReady ? '1' : '0'}
      >
        {/* Header block */}
        <table className="mb-4 w-full border-collapse">
          <tbody>
            <tr>
              <td className={`${tdStyle} w-1/6 font-bold`}>Date Created</td>
              <td className={`${tdStyle} w-1/4`}>{formatDate(header?.dateCreated)}</td>
              <td
                className={`${tdStyle} text-center align-middle`}
                rowSpan={companyRowSpan}
                data-tenant-logo="1"
              >
                <div className="flex flex-col items-center justify-center gap-1.5 px-2 py-2">
                  {tenantLogoUrl ? (
                    <img
                      src={tenantLogoUrl}
                      alt={companyLabel}
                      className="h-28 max-h-28 w-auto max-w-[220px] object-contain"
                      onLoad={() => setLogoReady(true)}
                      onError={() => setLogoReady(true)}
                    />
                  ) : null}
                  <span className="text-[10px] font-semibold uppercase leading-tight tracking-wide text-gray-700">
                    {companyLabel}
                  </span>
                </div>
              </td>
            </tr>
            <tr>
              <td className={`${tdStyle} font-bold`}>Document Owner</td>
              <td className={tdStyle}>{header?.documentOwner || '—'}</td>
            </tr>
            <tr>
              <td className={`${tdStyle} font-bold`}>Client</td>
              <td className={tdStyle}>{header?.client || '—'}</td>
            </tr>
            <tr>
              <td className={`${tdStyle} font-bold`}>Project</td>
              <td className={tdStyle}>{header?.project || '—'}</td>
            </tr>
            {versionLabel ? (
              <tr>
                <td className={`${tdStyle} font-bold`}>Document Version</td>
                <td className={tdStyle}>{versionLabel}</td>
              </tr>
            ) : null}
          </tbody>
        </table>

        {invoiceLabel && <p className="mb-3 text-right text-[11px] font-semibold text-gray-500">{invoiceLabel}</p>}

        {/* Dynamic sections */}
        {(sections || []).map((section, sIdx) => {
          const letter = sectionLetter(sIdx);
          const sectionTotals = pricedSectionTotals.find((s) => s.id === section.id);
          const dimVisibility = section.isPriced
            ? getSectionDimensionVisibility(section)
            : { showWidth: false, showHeight: false, showDepth: false };
          const { showWidth, showHeight, showDepth } = dimVisibility;
          const dimColCount =
            (showWidth ? 1 : 0) + (showHeight ? 1 : 0) + (showDepth ? 1 : 0);
          const pricedColCount = 7 + dimColCount; // Room, Unit, dims, SFT, Rate, Total, Details, Image
          const footerLabelColSpan = 4 + dimColCount; // through Rate
          const footerValueColSpan = 3; // Total, Details, Image
          const detailsWidth = `${26 + (3 - dimColCount) * 6}%`;

          return (
            <div key={section.id} className="mb-4" style={{ pageBreakInside: 'avoid' }}>
              <table className="w-full border-collapse">
                {section.isPriced ? (
                  <colgroup>
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '16%' }} />
                    {showWidth && <col style={{ width: '6%' }} />}
                    {showHeight && <col style={{ width: '6%' }} />}
                    {showDepth && <col style={{ width: '6%' }} />}
                    <col style={{ width: '6%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: detailsWidth }} />
                    <col style={{ width: '8%' }} />
                  </colgroup>
                ) : (
                  <colgroup>
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '19%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '49%' }} />
                    <col style={{ width: '8%' }} />
                  </colgroup>
                )}
                <thead>
                  <tr>
                    <th colSpan={section.isPriced ? pricedColCount : 5} className={sectionTitleStyle}>
                      <div className="flex items-center justify-between gap-3">
                        <span>
                          ({letter}) {section.name || 'Untitled section'}
                        </span>
                        {section.isPriced && sectionTotals ? (
                          <span className="whitespace-nowrap tabular-nums">
                            {formatCurrency(sectionTotals.afterDiscount)}
                          </span>
                        ) : null}
                      </div>
                    </th>
                  </tr>
                  {section.isPriced ? (
                    <tr>
                      <th className={thStyle}>Room</th>
                      <th className={thStyle}>Unit Name</th>
                      {showWidth && <th className={thStyle}>Width (ft)</th>}
                      {showHeight && <th className={thStyle}>Height (ft)</th>}
                      {showDepth && <th className={thStyle}>Depth (ft)</th>}
                      <th className={thStyle}>SFT</th>
                      <th className={thStyle}>Rate</th>
                      <th className={thStyle}>Total</th>
                      <th className={thStyle}>Details</th>
                      <th className={thStyle}>Image</th>
                    </tr>
                  ) : (
                    <tr>
                      <th className={thStyle}>Room / Category</th>
                      <th className={thStyle}>Unit Name</th>
                      <th className={thStyle}>Qty</th>
                      <th className={thStyle}>Details</th>
                      <th className={thStyle}>Image</th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {section.rooms.map((room) => (
                    <React.Fragment key={room.id}>
                      {renderRoomRows(room, section.isPriced, siteId, forExport, dimVisibility)}
                    </React.Fragment>
                  ))}
                </tbody>
                {section.isPriced && sectionTotals && (
                  <tfoot>
                    <tr>
                      <td colSpan={footerLabelColSpan} className={`${tdStyle} text-right font-bold`}>
                        Total ({letter})
                      </td>
                      <td colSpan={footerValueColSpan} className={`${tdStyle} font-bold`}>
                        {formatCurrency(sectionTotals.total)}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={footerLabelColSpan} className={`${tdStyle} text-right font-bold`}>
                        Discount ({sectionTotals.discountPercent || 0}%)
                      </td>
                      <td colSpan={footerValueColSpan} className={tdStyle}>
                        {formatCurrency(sectionTotals.discountAmount)}
                      </td>
                    </tr>
                    <tr>
                      <td
                        colSpan={footerLabelColSpan}
                        className={`${tdStyle} bg-gray-50 text-right font-bold`}
                      >
                        Total After Discount ({letter})
                      </td>
                      <td colSpan={footerValueColSpan} className={`${tdStyle} bg-gray-50 font-bold`}>
                        {formatCurrency(sectionTotals.afterDiscount)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          );
        })}

        {/* Grand totals */}
        <table className="mb-4 w-full border-collapse">
          <tbody>
            <tr>
              <td className={`${tdStyle} w-3/4 text-right font-bold`}>Total (Priced Sections)</td>
              <td className={`${tdStyle} font-bold`}>{formatCurrency(totalPriced)}</td>
            </tr>
            <tr>
              <td className={`${tdStyle} text-right font-bold`}>Total After Discount</td>
              <td className={tdStyle}>{formatCurrency(totalAfterDiscount)}</td>
            </tr>
            <tr>
              <td className={`${tdStyle} text-right font-bold`}>Design &amp; Coordination Fee ({designFeePercent}%)</td>
              <td className={tdStyle}>{formatCurrency(designFeeAmount)}</td>
            </tr>
            <tr>
              <td className={`${tdStyle} text-right font-bold`}>Amount with Design Fee</td>
              <td className={tdStyle}>{formatCurrency(amountWithDesignFee)}</td>
            </tr>
            <tr>
              <td className={`${tdStyle} text-right font-bold`}>GST ({gstPercent}%)</td>
              <td className={tdStyle}>{formatCurrency(gstAmount)}</td>
            </tr>
            <tr>
              <td className={`${tdStyle} bg-teal-50 text-right text-[13px] font-bold`}>FINAL AMOUNT INCL. TAXES</td>
              <td className={`${tdStyle} bg-teal-50 text-[13px] font-bold`}>{formatCurrency(finalAmount)}</td>
            </tr>
          </tbody>
        </table>

        {/* Payment schedule */}
        {paymentRows?.length > 0 && (
          <table className="mb-4 w-full border-collapse">
            <colgroup>
              <col style={{ width: '10%' }} />
              <col style={{ width: '60%' }} />
              <col style={{ width: '30%' }} />
            </colgroup>
            <thead>
              <tr>
                <th className={thStyle}>%</th>
                <th className={thStyle}>Payment Milestone</th>
                <th className={thStyle}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {paymentRows.map((row) => (
                <tr key={row.id}>
                  <td className={tdStyle}>{row.percent}%</td>
                  <td className={tdStyle}>{row.label}</td>
                  <td className={`${tdStyle} font-semibold`}>{formatCurrency(row.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Template text blocks, rendered as numbered rows */}
        {templateSections.map(([title, rows]) =>
          rows.length > 0 ? (
            <div key={title} className="mb-4" style={{ pageBreakInside: 'avoid' }}>
              <table className="w-full border-collapse">
                <colgroup>
                  <col style={{ width: '6%' }} />
                  <col style={{ width: '94%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th colSpan={2} className={sectionTitleStyle}>
                      {title}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((rowHtml, idx) => (
                    <tr key={idx}>
                      <td className={`${tdStyle} text-center`}>{idx + 1}</td>
                      {/* rowHtml is sanitized in htmlToNumberedRows before it ever reaches state */}
                      <td className={tdStyle} dangerouslySetInnerHTML={{ __html: rowHtml }} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null
        )}
      </div>
    );
  }
);
InteriorQuotationDocument.displayName = 'InteriorQuotationDocument';

// Rasterizes `node` with html2canvas and slices into A4 pages, then appends one full-size
// image page per line-item photo. Eye icons in the table link to those pages so full images
// open on any system (no localhost / login / network).
export const exportNodeToPdf = async (node, fileName) => {
  if (!node) {
    throw new Error('No node to export');
  }

  await waitForPdfImageCells(node);
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

  let logoDataUrl = null;
  const fetchedLogo = await getTenantLogoDataUrl().catch(() => null);
  if (isTenantLogoDataUrl(fetchedLogo)) {
    logoDataUrl = fetchedLogo;
    await applyTenantLogoDataUrlToNode(node, logoDataUrl);
  }

  const hotspots = gatherPdfImageHotspots(node);
  const docWidthPx = node.scrollWidth || node.offsetWidth || 794;

  const host = document.createElement('div');
  host.setAttribute('data-interior-pdf-export', 'true');
  host.style.cssText =
    'position:fixed;left:0;top:0;z-index:-1;pointer-events:none;opacity:0.01;';
  const clone = node.cloneNode(true);
  clone.style.width = `${docWidthPx}px`;
  host.appendChild(clone);
  document.body.appendChild(host);

  try {
    const canvas = await html2canvas(clone, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      windowWidth: clone.scrollWidth || docWidthPx,
      logging: false,
      onclone: (clonedDoc) => {
        const exported = clonedDoc.querySelector('[data-interior-pdf-export="true"]');
        if (exported) exported.style.opacity = '1';
        clonedDoc.querySelectorAll('[data-tenant-logo="1"] img').forEach((el) => {
          el.style.visibility = 'hidden';
        });
      },
    });

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const imgData = canvas.toDataURL('image/png', 1.0);

    let heightLeft = imgHeight;
    let position = 0;
    let pageCount = 1;
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    while (heightLeft > 0) {
      position -= pageHeight;
      pdf.addPage();
      pageCount += 1;
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    stampTenantLogoOnPdf(pdf, node, logoDataUrl, pageWidth);

    // One full-size image page per photo; map eye hotspot → that page.
    const keyToPage = new Map();
    const uniqueKeys = [];
    const seenKeys = new Set();
    hotspots.forEach((h) => {
      if (!seenKeys.has(h.key) && pdfExportImages.has(h.key)) {
        seenKeys.add(h.key);
        uniqueKeys.push(h.key);
      }
    });

    for (const key of uniqueKeys) {
      const entry = pdfExportImages.get(key);
      if (!entry?.dataUrl) continue;
      await addFullImagePage(pdf, entry.dataUrl, entry.title);
      pageCount += 1;
      keyToPage.set(key, pageCount);
    }

    const mmPerPx = pageWidth / docWidthPx;
    hotspots.forEach((spot) => {
      const targetPage = keyToPage.get(spot.key);
      if (!targetPage) return;
      const xMm = spot.x * mmPerPx;
      const yDocMm = spot.y * mmPerPx;
      const wMm = Math.max(spot.w * mmPerPx, 5);
      const hMm = Math.max(spot.h * mmPerPx, 5);
      const mainPageCount = pageCount - uniqueKeys.length;
      const pageIndex = Math.min(Math.floor(yDocMm / pageHeight), Math.max(mainPageCount - 1, 0));
      const yOnPage = yDocMm - pageIndex * pageHeight;
      if (yOnPage + hMm < 0 || yOnPage > pageHeight) return;
      pdf.setPage(pageIndex + 1);
      pdf.link(xMm, yOnPage, wMm, hMm, { pageNumber: targetPage });
    });

    pdf.save(fileName);
  } finally {
    host.remove();
  }
};

const PreviewInteriorQuatation = (props) => {
  const { isOpen, onClose, header } = props;
  const contentRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false);
  // Bump after PDF export so eye icons remount if html2canvas ever touched the live tree.
  const [documentKey, setDocumentKey] = useState(0);

  // position: fixed is only viewport-relative if no ancestor has a transform - Framer Motion
  // leaves one on its motion.div wrappers even at rest, which would otherwise clip this modal
  // to that box. Locking body scroll + portaling straight to <body> sidesteps both issues.
  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleDownloadPdf = async () => {
    if (isGenerating) return;
    try {
      setIsGenerating(true);

      // Render a separate off-screen copy for capture — never run html2canvas on the
      // visible preview (that was wiping lucide eye SVGs / breaking image open).
      const {
        isOpen: _isOpen,
        onClose: _onClose,
        ...docProps
      } = props;

      const fetchedLogo = await getTenantLogoDataUrl().catch(() => null);
      const logoUrl = isTenantLogoDataUrl(fetchedLogo) ? fetchedLogo : undefined;

      const container = document.createElement('div');
      container.style.cssText =
        'position:fixed;left:0;top:0;z-index:-1;pointer-events:none;opacity:0.01;';
      document.body.appendChild(container);
      const root = createRoot(container);
      const exportRef = React.createRef();

      try {
        resetPdfExportImages();
        await new Promise((resolve) => {
          root.render(
            React.createElement(InteriorQuotationDocument, {
              ref: exportRef,
              ...docProps,
              forExport: true,
              logoUrl,
            })
          );
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        });

        if (!exportRef.current) {
          throw new Error('Unable to render quotation document for PDF.');
        }

        await waitForPdfImageCells(exportRef.current);

        const clientSlug = (header?.client || 'quotation').trim().replace(/\s+/g, '-');
        const versionSlug = props.documentVersion
          ? `-${String(props.documentVersion).replace(/^v/i, 'v')}`
          : '';
        await exportNodeToPdf(
          exportRef.current,
          `${clientSlug}${versionSlug}-interior-quotation.pdf`
        );
      } finally {
        root.unmount();
        container.remove();
        resetPdfExportImages();
      }
    } catch (error) {
      console.error('Failed to generate interior quotation PDF', error);
      alert('Unable to generate PDF. Please try again.');
    } finally {
      setIsGenerating(false);
      setDocumentKey((k) => k + 1);
    }
  };

  return createPortal(
    // color-scheme: dark tells the browser's own auto-dark-mode repaint (Edge/Chrome "auto
    // dark for web content", or OS-level forced dark) that this subtree is intentionally
    // dark-themed, so it stops trying to "fix" contrast here - without it, Chromium's
    // heuristic can misjudge an already-dark panel like this one (dark bg + light text,
    // sitting inside an otherwise light-themed app) and invert the text back to near-black,
    // which no ordinary CSS color/`!important` can prevent since it repaints after layout.
    <div
      className="fixed inset-0 z-[9999] flex h-screen w-screen flex-col bg-gray-800"
      style={{ colorScheme: 'dark' }}
    >
      <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-700 bg-gray-900 px-6 py-4">
        <div>
          <h2 className="text-base font-semibold text-white">Interior Quotation Preview</h2>
          <p className="text-xs text-gray-400">
            {header?.client || 'Untitled client'} &middot; {header?.project || 'Untitled project'}
            {props.documentVersion
              ? ` · ${
                  String(props.documentVersion).toLowerCase().startsWith('v')
                    ? props.documentVersion
                    : `v${props.documentVersion}`
                }`
              : ''}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={isGenerating}
            className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {isGenerating ? 'Generating...' : 'Download PDF'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-600 px-4 py-2 text-sm font-semibold text-gray-200 transition hover:bg-gray-700"
          >
            <X className="h-4 w-4" /> Close
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-gray-600 p-6">
        <div className="flex items-start justify-center">
          <InteriorQuotationDocument key={documentKey} ref={contentRef} {...props} />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PreviewInteriorQuatation;
